import { AxiosAdapter } from 'axios';
import { User } from 'oidc-client-ts';

import { services } from 'services/base';
import {
  ClearCacheBeforeService,
  GetCacheStateService,
  OfflineCacheState,
  OnCacheStateChangeService,
  OnCacheStateChangeServiceCallback,
  OnOnlineChangeService,
  OnOnlineChangeServiceCallback,
  SetOfflineContainerService,
  UpdateOfflineCacheAdapterService,
  UpdateOfflineUserService,
} from 'types';
import { createOfflineCacheAdapter, encryptedTimestampCache, getOfflineUser, setOfflineUser } from 'utils';

type OfflineState = {
  online: boolean;
  cache: OfflineCacheState;
  offlineContainer: HTMLElement | null;
};

const SERVICE_PREFIX = 'appFrame.offline';
const ACTION_OFFLINE_ONLINE_CHANGE = 'AppFrame:Offline:OnlineChange';
const ACTION_OFFLINE_CACHE_STATE_CHANGE = 'AppFrame:Offline:CacheStateChange';

const handleOnline = () => services.executeAction(ACTION_OFFLINE_ONLINE_CHANGE, true);
const handleOffline = () => services.executeAction(ACTION_OFFLINE_ONLINE_CHANGE, false);

const initOfflineServices = () => {
  const { registerService, executeAction, registerAction, clearAction } = services;
  const handleHashChange = () => {
    const { hash } = window.location;
    executeAction(ACTION_OFFLINE_CACHE_STATE_CHANGE, {
      found: true,
      actualPath: hash,
      lastUsedPath: hash,
    });
  };

  const getUserId = (user: User) => (user.profile.preferred_username as string) || '';

  const state: OfflineState = {
    online: window.navigator.onLine,
    cache: {
      found: true,
      offlineContainerStyle: {
        position: 'absolute',
        zIndex: '100',
      },
      actualPath: null,
      lastUsedPath: null,
    },
    offlineContainer: null,
  };

  const setCacheResponseExist = (exist: boolean) => {
    // if cache response exists, but path not changed than hold the state, because some response failed to this path
    if (!state.cache.found || exist) {
      return;
    }

    const offlineRect = state.offlineContainer?.getBoundingClientRect();
    // The setted offline element maybe not visible
    const rect = offlineRect?.height ? offlineRect : services.appFrame.viewport.getMainNode()?.getBoundingClientRect();
    const actStyle = rect
      ? {
          top: rect.y,
          left: rect.x,
          width: rect.width,
          height: rect.height,
        }
      : undefined;

    executeAction(ACTION_OFFLINE_CACHE_STATE_CHANGE, {
      found: false,
      lastUsedPath: state.cache.actualPath,
      offlineContainerStyle: {
        ...state.cache.offlineContainerStyle,
        ...actStyle,
      },
    });
  };
  registerAction(
    ACTION_OFFLINE_CACHE_STATE_CHANGE,
    (cacheState: Partial<OfflineCacheState>) => {
      state.cache = { ...state.cache, ...cacheState };
    },
    { order: 0 },
  );
  registerService<GetCacheStateService>(`${SERVICE_PREFIX}.getCacheState`, () => state.cache);
  registerService<OnCacheStateChangeService>(
    `${SERVICE_PREFIX}.onCacheStateChange`,
    (callback: OnCacheStateChangeServiceCallback) => {
      registerAction(ACTION_OFFLINE_CACHE_STATE_CHANGE, callback, { order: 1 });
      return () => clearAction(ACTION_OFFLINE_CACHE_STATE_CHANGE, callback);
    },
  );

  registerService<UpdateOfflineCacheAdapterService>(
    `${SERVICE_PREFIX}.updateOfflineCacheAdapter`,
    (originalAdapter: AxiosAdapter, autoShowOfflineErrors?: boolean) => {
      return createOfflineCacheAdapter(originalAdapter, autoShowOfflineErrors ? setCacheResponseExist : undefined);
    },
  );

  registerService<UpdateOfflineUserService>(
    `${SERVICE_PREFIX}.updateOfflineUser`,
    async (user: User | null, moduleId: string | null = null) => {
      // if user logged out, it can be null
      if (!user) {
        return;
      }

      const userId = getUserId(user);
      const cacheUser = await getOfflineUser(moduleId);
      if (cacheUser && getUserId(cacheUser) !== userId) {
        // clear cached values if user changed
        await encryptedTimestampCache.clearCache();
      }
      // cache the user with actual token and other properties
      await setOfflineUser(user, moduleId);
      encryptedTimestampCache.setPassword(userId);
    },
  );

  registerService<SetOfflineContainerService>(`${SERVICE_PREFIX}.setOfflineContainer`, (container: HTMLElement) => {
    state.offlineContainer = container;
  });

  registerService<ClearCacheBeforeService>(`${SERVICE_PREFIX}.clearCacheBefore`, async (clearBeforeDayNo?: number) =>
    encryptedTimestampCache.clearCacheBefore(clearBeforeDayNo),
  );

  // we remove event listeners first to avoid mulitple subscription on reinit
  window.removeEventListener('online', handleOnline);
  window.addEventListener('online', handleOnline);

  window.removeEventListener('offline', handleOffline);
  window.addEventListener('offline', handleOffline);

  registerService(`${SERVICE_PREFIX}.isOnline`, () => state.online);
  registerAction(
    ACTION_OFFLINE_ONLINE_CHANGE,
    (online: boolean) => {
      state.online = online;
    },
    { order: 0 },
  );
  registerService<OnOnlineChangeService>(
    `${SERVICE_PREFIX}.onOnlineChange`,
    (callback: OnOnlineChangeServiceCallback) => {
      registerAction(ACTION_OFFLINE_ONLINE_CHANGE, callback, { order: 1 });
      return () => clearAction(ACTION_OFFLINE_ONLINE_CHANGE, callback);
    },
  );

  window.removeEventListener('hashchange', handleHashChange);
  window.addEventListener('hashchange', handleHashChange);
};

export default initOfflineServices;
